import WarningIcon from "@/assets/colorable/warning.svg?react";
import RefluencedLogo from "@/assets/images/refluenced.svg";
import FileUpload from "@/components/Common/FileUpload";
import GenerativeAiLock from "@/components/Common/GenerativeAiLock";
import AddressForm from "@/components/Common/forms/AddressForm";
import AutosizeTextarea from "@/components/Common/forms/AutosizeTextarea";
import SocialField from "@/components/Common/forms/SocialField";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import {PhoneInput} from "@/components/ui/PhoneInput";
import useCountries from "@/hooks/useCountries";
import type {TCustomerCreation, TCustomerState} from "@/hooks/useCustomerCreation";
import {useGenerativeAiAllowed} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import OnboardingCta from "@/pages/registerCompany/OnboardingCta";
import RegistrationInputField from "@/pages/registerCompany/RegistrationInputField";
import InterestSelection from "@/pages/registerCompany/steps/interestSelection/InterestSelection";
import useRegistrationStore from "@/store/registration";
import Rive from "@rive-app/react-canvas";
import {type FC} from "react";
import {useTranslation} from "react-i18next";
import {useShallow} from "zustand/shallow";

const CreateCustomerContent: FC<{compact?: boolean; customerCreation: TCustomerCreation; isLargeBrand?: boolean}> = ({
  compact = false,
  customerCreation,
  isLargeBrand = false,
}) => {
  const {t} = useTranslation(["multi_account", "common"]);
  const {getCountryCodeByEnLabel} = useCountries();
  const generativeAiAllowed = useGenerativeAiAllowed();
  const {companyInstagramUsername: instagramError, companyTikTokUsername: tiktokError} = useRegistrationStore(
    state => state.errors,
  );
  const [insta, tiktok, interests] = useRegistrationStore(
    useShallow(state => [state.companyInstagramUsername, state.companyTikTokUsername, state.interests]),
  );
  const setRegistrationValue = useRegistrationStore(state => state.setValue);
  const setRegistrationError = useRegistrationStore(state => state.setError);

  return (
    <div className={compact ? "flex flex-col gap-8" : "contents"}>
      <div className={clsxm("flex w-full flex-col items-center", !compact && "pt-5")}>
        <div className={clsxm("flex w-full flex-col items-center", compact ? "gap-4" : "gap-8")}>
          {!compact && (
            <div className="flex flex-col items-center gap-3">
              <img src={RefluencedLogo} className="size-20 shrink-0 object-contain" />
              <p className="text-neutral-black m-0 text-sm font-semibold">
                {t(isLargeBrand ? "company_creation" : "customer_creation")}
              </p>
            </div>
          )}
          <div className="flex w-full flex-col gap-2">
            <GenerativeAiLock locked={!generativeAiAllowed} className="w-full">
              <OnboardingCta
                buttonClassName={compact ? "h-11 text-sm" : "h-15"}
                buttonContainerClassName={clsxm(customerCreation.isCrawlingInProgress && "basis-0 md:basis-0")}
                buttonTitle={t(isLargeBrand ? "common:create_company_with_ai" : "common:create_customer_with_ai")}
                className={clsxm("w-full", compact ? "gap-4 px-8" : "gap-8 px-5")}
                inputClassName={clsxm(compact ? "border-neutral-grey-light-dark h-11 text-sm" : "h-15")}
                isLoading={customerCreation.isCrawlingInProgress}
                value={customerCreation.state.website}
                setValue={v =>
                  customerCreation.setState(draft => {
                    draft.website = v;
                  })
                }
                onClick={customerCreation.startCrawling}
              />
            </GenerativeAiLock>
            {customerCreation.isCrawlingInProgress && (
              <SkeletonLoader
                className={clsxm(
                  "flex items-center justify-center gap-2 rounded-none border-none",
                  compact ? "h-11" : "h-15",
                )}>
                <Rive className="size-5 shrink-0" src="/onboarding/brain_shimmer.riv" />
                <span className="text-neutral-black text-sm font-semibold">{t("common:working_on_form")}</span>
              </SkeletonLoader>
            )}
            {customerCreation.showCrawlingDisclaimer && (
              <div
                data-testid="crawling-disclaimer"
                className={clsxm(
                  "bg-warning-light-yellow flex w-full items-center justify-center gap-3 rounded-md p-3",
                  compact ? "min-h-11" : "min-h-15",
                )}>
                <WarningIcon className="text-warning-dark-yellow size-4.5 shrink-0" />
                <span className="text-neutral-black text-xs">{t("common:some_fields_couldnt_be_filled_in")}</span>
              </div>
            )}
          </div>
        </div>
        <span
          className={clsxm(
            "text-neutral-black",
            compact ? "pt-8 text-sm font-medium" : "py-8 text-xl leading-5 font-semibold",
          )}>
          {t("common:or_manually_fill_out_below")}
        </span>
      </div>
      <div className="bg-primary-powder flex flex-col">
        <div className="flex flex-col">
          <div
            className={clsxm(
              "flex flex-col items-center",
              compact ? "gap-3 px-8 py-2" : "gap-5 p-5 pb-2.5",
              customerCreation.isCrawledFieldEmpty(customerCreation.state.file) && "bg-warning-light-yellow",
            )}>
            <span className={clsxm("text-neutral-black", compact ? "text-sm font-medium" : "text-xl font-semibold")}>
              {t(isLargeBrand ? "add_your_companies_logo_here" : "add_your_customer_logo_here")}
            </span>
            <FileUpload
              className="border-neutral-grey-light-dark"
              disabled={customerCreation.isCrawlingInProgress}
              file={customerCreation.state?.file}
              isLoading={customerCreation.isCrawlingInProgress}
              onUpload={files =>
                customerCreation.setState(draft => {
                  draft.file = files[0];
                })
              }
            />
          </div>
          <div
            className={clsxm(
              "flex w-full flex-col gap-1",
              compact ? "px-8 py-2" : "px-5 py-2.5",
              customerCreation.isCrawledFieldEmpty(customerCreation.state.company_name) && "bg-warning-light-yellow",
            )}>
            <label
              htmlFor="firstName"
              className={
                compact ? "text-neutral-black text-sm font-medium" : "text-neutral-grey-dark text-sm font-medium"
              }>
              {t(isLargeBrand ? "company_name" : "customers_name")}
            </label>
            <RegistrationInputField
              className={clsxm("border-neutral-grey-light-dark", compact ? "h-11 rounded-lg px-3 text-sm" : "h-15")}
              disabled={customerCreation.isCrawlingInProgress}
              isLoading={customerCreation.isCrawlingInProgress && !customerCreation.state.company_name}
              storeKey="firstName"
              value={customerCreation.state.company_name}
              onChange={v =>
                customerCreation.setState(draft => {
                  draft.company_name = v;
                })
              }
              placeholder={t(isLargeBrand ? "your_companies_name" : "your_customer_name")}
            />
          </div>
          <AddressForm
            cityKey="address_city"
            className={clsxm("gap-1", compact ? "px-8 py-2" : "px-5 py-2.5")}
            countryKey="country"
            errors={customerCreation.errors}
            inputClassName={clsxm(
              "border-neutral-grey-light-dark bg-white",
              compact ? "h-11 rounded-lg px-3 text-sm" : "h-15",
            )}
            labelClassName={compact ? "text-neutral-black text-sm font-medium" : undefined}
            onDynamicAddressBlur={() => customerCreation.setDynamicAddressFocused(false)}
            onDynamicAddressFocus={() => customerCreation.setDynamicAddressFocused(true)}
            optional
            placeDescriptionKey="place_description"
            placeIdKey="place_id"
            postalKey="address_postal"
            storeForm={customerCreation.state}
            storeSetter={<K extends keyof TCustomerState>(key: K, value: TCustomerState[K]) => {
              customerCreation.setState(draft => {
                draft[key] = value;
              });
            }}
            streetKey="address_street"
            title={t(isLargeBrand ? "companies_address" : "customer_address")}
            titleClassName={compact ? "text-neutral-black text-sm font-medium" : undefined}
          />
          <PhoneInput
            buttonClassName={clsxm("bg-white border-neutral-grey-light-dark", compact ? " h-11 w-22 " : "h-15")}
            containerClassName={compact ? "px-8 py-2" : "px-5 py-2.5"}
            defaultCountry={
              customerCreation.state.country && !customerCreation.state.phonenumber
                ? getCountryCodeByEnLabel(customerCreation.state.country)
                : "CH"
            }
            label={t(isLargeBrand ? "companies_phone_number" : "customer_phone_number")}
            labelClassName={compact ? "text-neutral-black text-sm font-medium" : undefined}
            numberInputClassName={clsxm(
              "border-neutral-grey-light-dark bg-white",
              compact ? "h-11  px-3 text-sm md:text-sm" : "h-15",
            )}
            onChange={v =>
              customerCreation.setState(draft => {
                draft.phonenumber = v;
              })
            }
            optional
            value={customerCreation.state.phonenumber}
          />
          <div className={compact ? "px-8 py-2" : "px-5 py-2.5"}>
            <RegistrationInputField
              className={clsxm("border-neutral-grey-light-dark", compact ? "h-11 rounded-lg px-3 text-sm" : "h-15")}
              value={customerCreation.state.website}
              onChange={v =>
                customerCreation.setState(draft => {
                  draft.website = v;
                })
              }
              optional
              storeKey="companyWebsite"
              label={t(isLargeBrand ? "companies_website" : "customer_website")}
              labelClassName={compact ? "text-neutral-black text-sm font-medium" : undefined}
              placeholder={t(
                isLargeBrand ? "common:companiesWebsitePlaceholder" : "common:customer_website_placeholder",
              )}
            />
          </div>
          <div
            className={clsxm(
              compact ? "px-8 py-2" : "px-5 py-2.5",
              customerCreation.isCrawledFieldEmpty(insta) && "bg-warning-light-yellow",
            )}>
            <SocialField
              className={clsxm(
                compact ? "h-11 rounded-lg text-sm" : "h-15",
                !instagramError && "border-neutral-grey-light-dark",
              )}
              dataTestId="create-customer-instagram-field"
              error={instagramError}
              isDisabled={customerCreation.isCrawlingInProgress}
              isLoading={customerCreation.isCrawlingInProgress && !insta}
              label={t(isLargeBrand ? "companies_instagram" : "customer_instagram")}
              onChange={value => {
                setRegistrationValue("companyInstagramUsername", value);
              }}
              placeholder={t(
                isLargeBrand
                  ? "common:companiesInstagramUsernamePlaceholder"
                  : "common:customer_instagram_username_placeholder",
              )}
              setError={message => setRegistrationError("companyInstagramUsername", message ?? "")}
              type="instagram"
              value={insta}
            />
          </div>
          <div
            className={clsxm(
              compact ? "px-8 py-2" : "px-5 py-2.5",
              customerCreation.isCrawledFieldEmpty(tiktok) && "bg-warning-light-yellow",
            )}>
            <SocialField
              className={clsxm(
                compact ? "h-11 rounded-lg text-sm" : "h-15",
                !tiktokError && "border-neutral-grey-light-dark",
              )}
              error={tiktokError}
              isDisabled={customerCreation.isCrawlingInProgress}
              isLoading={customerCreation.isCrawlingInProgress && !tiktok}
              label={t(isLargeBrand ? "companies_tiktok" : "customer_tiktok")}
              onChange={value => {
                if (!insta) {
                  setRegistrationError("companyInstagramUsername", "");
                }

                setRegistrationValue("companyTikTokUsername", value);
              }}
              optional
              placeholder={t(
                isLargeBrand
                  ? "common:companiesTikTokUsernamePlaceholder"
                  : "common:customer_tiktok_username_placeholder",
              )}
              setError={message => setRegistrationError("companyTikTokUsername", message ?? "")}
              type="tiktok"
              value={tiktok}
            />
          </div>
        </div>
        <div className="flex w-full flex-col">
          <div
            className={clsxm(
              compact ? "px-8 py-2" : "p-5 pt-8",
              customerCreation.isCrawledFieldEmpty(interests?.length) && "bg-warning-light-yellow",
            )}>
            <InterestSelection
              compact={compact}
              isLoading={customerCreation.isCrawlingInProgress}
              label={isLargeBrand ? "selectInterestsLabel" : "customer_interests"}
            />
          </div>
          <div
            className={clsxm(
              compact ? "px-8 py-2" : "p-5",
              customerCreation.isCrawledFieldEmpty(customerCreation.state.bio) && "bg-warning-light-yellow",
            )}>
            <AutosizeTextarea
              className={
                compact ? "border-neutral-grey-light-dark rounded-lg p-3 text-sm md:placeholder:text-sm" : undefined
              }
              disabled={customerCreation.isCrawlingInProgress}
              htmlFor="bio"
              isLoading={customerCreation.isCrawlingInProgress && !customerCreation.state.bio}
              minRows={1}
              maxRows={4}
              onChange={e =>
                customerCreation.setState(draft => {
                  draft.bio = e.target.value;
                })
              }
              optional
              placeholder={t(isLargeBrand ? "company_bio_goes_here" : "customer_bio_goes_here")}
              title={t(isLargeBrand ? "write_a_short_bio_for_your_company" : "write_a_short_bio")}
              titleClassName={compact ? "text-neutral-black text-sm font-medium" : undefined}
              value={customerCreation.state.bio}
            />
          </div>
          <div
            className={clsxm(
              compact ? "px-8 py-2" : "p-5",
              customerCreation.isCrawledFieldEmpty(customerCreation.state.company_overview) &&
                "bg-warning-light-yellow",
            )}>
            <AutosizeTextarea
              className={
                compact ? "border-neutral-grey-light-dark rounded-lg p-3 text-sm md:placeholder:text-sm" : undefined
              }
              disabled={customerCreation.isCrawlingInProgress}
              htmlFor="companyOverview"
              isLoading={customerCreation.isCrawlingInProgress && !customerCreation.state.company_overview}
              minRows={1}
              maxRows={4}
              onChange={e =>
                customerCreation.setState(draft => {
                  draft.company_overview = e.target.value;
                })
              }
              optional
              placeholder={t(isLargeBrand ? "company_overview_goes_here" : "customer_overview_goes_here")}
              title={t(isLargeBrand ? "common:company_overview" : "common:customer_overview")}
              titleClassName={compact ? "text-neutral-black text-sm font-medium" : undefined}
              value={customerCreation.state.company_overview ?? ""}
            />
          </div>
          <div
            className={clsxm(
              compact ? "px-8 py-2" : "p-5",
              customerCreation.isCrawledFieldEmpty(customerCreation.state.products_overview) &&
                "bg-warning-light-yellow",
            )}>
            <AutosizeTextarea
              className={
                compact ? "border-neutral-grey-light-dark rounded-lg p-3 text-sm md:placeholder:text-sm" : undefined
              }
              disabled={customerCreation.isCrawlingInProgress}
              htmlFor="productOverview"
              isLoading={customerCreation.isCrawlingInProgress && !customerCreation.state.products_overview}
              minRows={1}
              maxRows={4}
              onChange={e =>
                customerCreation.setState(draft => {
                  draft.products_overview = e.target.value;
                })
              }
              optional
              placeholder={t("product_overview_goes_gere")}
              title={t("common:product_overview")}
              titleClassName={compact ? "text-neutral-black text-sm font-medium" : undefined}
              value={customerCreation.state.products_overview ?? ""}
            />
          </div>
          <div
            className={clsxm(
              compact ? "px-8 py-2" : "p-5",
              customerCreation.isCrawledFieldEmpty(customerCreation.state.customer_pain_points) &&
                "bg-warning-light-yellow",
            )}>
            <AutosizeTextarea
              className={
                compact ? "border-neutral-grey-light-dark rounded-lg p-3 text-sm md:placeholder:text-sm" : undefined
              }
              disabled={customerCreation.isCrawlingInProgress}
              htmlFor="customerPainPoints"
              isLoading={customerCreation.isCrawlingInProgress && !customerCreation.state.customer_pain_points}
              minRows={1}
              maxRows={4}
              onChange={e =>
                customerCreation.setState(draft => {
                  draft.customer_pain_points = e.target.value;
                })
              }
              optional
              placeholder={t("customer_pain_points_goes_here")}
              title={t("common:customer_pain_points")}
              titleClassName={compact ? "text-neutral-black text-sm font-medium" : undefined}
              value={customerCreation.state.customer_pain_points ?? ""}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCustomerContent;
